import { collapseWeeklySlotsToWindows, type WeekdayIndex } from '@/utils/hourlyBudgetWeek';
import type { HourSlotMap } from '@/utils/hourlyBudgetHourSlots';
import type { HourlyBudgetGroupPayload } from '@/types/hourlyBudget';

export type DaypartingKind = 'budget' | 'bid' | 'placement';

export type BidMethod =
  | 'increase_pct'
  | 'decrease_pct'
  | 'increase_amount'
  | 'decrease_amount'
  | 'set';

export type BidDaypartingAction = {
  method: BidMethod;
  value: number;
};

export type PlacementMode = 'SET' | 'INCREASE' | 'DECREASE';

export type PlacementAdjustment = {
  placement: 'TOP_OF_SEARCH' | 'REST_OF_SEARCH' | 'PRODUCT_PAGES';
  mode: PlacementMode;
  value: number;
};

export type PlacementDaypartingAction = {
  adjustments: PlacementAdjustment[];
};

export const BID_METHOD_OPTIONS: Array<{ id: BidMethod; label: string }> = [
  { id: 'increase_pct', label: 'Increase by %' },
  { id: 'decrease_pct', label: 'Decrease by %' },
  { id: 'increase_amount', label: 'Increase by amount' },
  { id: 'decrease_amount', label: 'Decrease by amount' },
  { id: 'set', label: 'Set bid' },
];

export const PLACEMENT_FIELD_OPTIONS: Array<{ id: PlacementAdjustment['placement']; label: string }> = [
  { id: 'TOP_OF_SEARCH', label: 'Top of Search' },
  { id: 'REST_OF_SEARCH', label: 'Rest of Search' },
  { id: 'PRODUCT_PAGES', label: 'Product Pages' },
];

export const PLACEMENT_MODE_OPTIONS: Array<{ id: PlacementMode; label: string }> = [
  { id: 'SET', label: 'Set' },
  { id: 'INCREASE', label: 'Increase by' },
  { id: 'DECREASE', label: 'Decrease by' },
];

export function validateBidAction(method: string, rawValue: string): string | null {
  const value = Number(rawValue);
  if (!rawValue.trim() || Number.isNaN(value) || value <= 0) {
    return 'Enter a value greater than 0.';
  }
  if (method === 'decrease_pct' && value > 100) {
    return 'Decrease percentage cannot exceed 100.';
  }
  if (!BID_METHOD_OPTIONS.some((option) => option.id === method)) {
    return 'Choose a bid change.';
  }
  return null;
}

export function validatePlacementRows(
  rows: Record<string, { mode: string; value: string }>
): string | null {
  const filled = PLACEMENT_FIELD_OPTIONS.filter((field) => rows[field.id]?.value.trim());
  if (filled.length === 0) return 'Enter at least one placement.';
  for (const field of filled) {
    const row = rows[field.id];
    const value = Number(row.value);
    if (Number.isNaN(value)) return `${field.label} needs a number.`;
    if ((row.mode === 'INCREASE' || row.mode === 'DECREASE') && value <= 0) {
      return `${field.label} must be greater than 0.`;
    }
    if (row.mode === 'SET' && (value < 0 || value > 900)) {
      return `${field.label} must be between 0 and 900.`;
    }
  }
  return null;
}

export function placementActionFromRows(
  rows: Record<string, { mode: string; value: string }>
): PlacementDaypartingAction {
  return {
    adjustments: PLACEMENT_FIELD_OPTIONS.flatMap((field) => {
      const row = rows[field.id];
      if (!row?.value.trim()) return [];
      return [
        {
          placement: field.id,
          mode: (row.mode || 'SET') as PlacementMode,
          value: Number(row.value),
        },
      ];
    }),
  };
}

export function summarizeBidAction(action: BidDaypartingAction | null | undefined): string {
  if (!action) return '—';
  const label = BID_METHOD_OPTIONS.find((option) => option.id === action.method)?.label ?? action.method;
  const suffix = action.method.endsWith('_pct') ? '%' : '';
  return `${label} ${action.value}${suffix}`;
}

export function summarizePlacementAction(action: PlacementDaypartingAction | null | undefined): string {
  if (!action?.adjustments?.length) return '—';
  return action.adjustments
    .map((row) => {
      const label = PLACEMENT_FIELD_OPTIONS.find((field) => field.id === row.placement)?.label ?? row.placement;
      const mode = PLACEMENT_MODE_OPTIONS.find((option) => option.id === row.mode)?.label ?? row.mode;
      return `${label} ${mode} ${row.value}`;
    })
    .join(', ');
}

export function buildBidPlacementGroupPayload(input: {
  kind: 'bid' | 'placement';
  active: boolean;
  slots: Record<WeekdayIndex, HourSlotMap>;
  action: BidDaypartingAction | PlacementDaypartingAction;
  sourceType: 'campaigns' | 'portfolio';
  campaignRefs?: HourlyBudgetGroupPayload['campaign_refs'];
  portfolioId?: string | null;
}): HourlyBudgetGroupPayload {
  const { bases } = collapseWeeklySlotsToWindows(input.slots);
  return {
    kind: input.kind,
    action: input.action,
    active: input.active,
    schedule_mode: 'weekly',
    hourly_budget: null,
    base_budget: null,
    day_settings: [],
    time_windows: [],
    base_windows: bases.map((window, index) => ({
      start_time_local: window.start_time_local,
      end_time_local: window.end_time_local,
      sort_order: index,
      days_of_week: window.days_of_week,
    })),
    source_type: input.sourceType,
    ...(input.sourceType === 'campaigns'
      ? { campaign_refs: input.campaignRefs ?? [] }
      : { portfolio_id: input.portfolioId ?? undefined }),
  };
}

export function selectedHourCount(slots: Record<number, HourSlotMap>): number {
  return Object.values(slots).reduce((sum, day) => sum + Object.keys(day).length, 0);
}
